// Web bridge for migrating the user's Electron-backed projects into
// web_scripts. The heavy lifting (downloading bundles, parsing git,
// inserting rows) lives in the `migrate-electron-projects` Edge
// Function — this module just POSTs to it and surfaces results.
//
// Trigger pattern: file.js's `backupListCloud` calls
// `ensureElectronImportOnce` the first time per page-load. If the
// Edge Function isn't deployed yet (404), we swallow the error so a
// fresh prototype doesn't choke on first sign-in.

let inFlight = null;
let attempted = false;

export function buildMigrateHandlers({ supabase, env }) {
  async function callMigrate({ dryRun = false } = {}) {
    if (!supabase) return { ok: false, error: 'Supabase not configured' };
    if (!env?.SUPABASE_URL) return { ok: false, error: 'Supabase URL not configured' };
    const { data: sess } = await supabase.auth.getSession();
    const jwt = sess?.session?.access_token;
    if (!jwt) return { ok: false, error: 'not signed in', code: 'auth-missing' };

    const url = `${env.SUPABASE_URL.replace(/\/$/, '')}/functions/v1/migrate-electron-projects`;
    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ dryRun }),
      });
    } catch (err) {
      return { ok: false, error: err?.message || 'network failed' };
    }
    let parsed = null;
    try {
      parsed = await res.json();
    } catch {
      /* empty */
    }
    if (!res.ok || !parsed?.ok) {
      return {
        ok: false,
        error: parsed?.error || `HTTP ${res.status}`,
        code: parsed?.code,
      };
    }
    return parsed;
  }

  return {
    migrateElectronProjects: callMigrate,

    // Called once per page load (after the first successful auth) so
    // a returning Electron user finds their cloud-backed projects
    // already populated in the welcome list without having to push a
    // "Migrate" button.
    ensureElectronImportOnce: async () => {
      if (attempted) return inFlight || { ok: true, skipped: true };
      attempted = true;
      inFlight = (async () => {
        const r = await callMigrate({ dryRun: false });
        // If the Edge Function isn't deployed yet, the call fails
        // with a 404 — that's fine for the prototype, just log and
        // move on so the welcome list still renders.
        if (!r.ok && r.error && /404|not found/i.test(r.error)) {
          console.warn('[migrate] Edge Function not deployed yet; skipping import.');
          return { ok: true, skipped: true };
        }
        if (!r.ok) {
          console.warn('[migrate] Electron import failed', r);
        } else if (r.migrated?.length) {
          console.log(`[migrate] imported ${r.migrated.length} Electron project(s)`);
        }
        return r;
      })();
      return inFlight;
    },
  };
}
